// FICHIER UNIQUE de configuration d'environnement.
// - Détection À L'EXÉCUTION (un seul bundle local/dev/main, sans rebuild).
// - Toutes les variables sont PRÉFIXÉES PAR DOMAINE :
//     AUTH_*  -> Entra External ID (CIAM)
//     API_*   -> backend cc-attack
//
// Ordre de résolution de l'env :
//   1) window.__CCJAW_ENV__   2) ?env=…   3) hostname   4) 'local'
import { Platform } from 'react-native';

import { NATIVE_ENV } from './env.generated';

export type AppEnv = 'local' | 'dev' | 'main';

export interface EnvConfig {
  env: AppEnv;
  // --- domaine AUTH (Entra External ID / CIAM) ---
  AUTH_TENANT_ID: string;
  AUTH_TENANT_SUBDOMAIN: string;
  AUTH_SPA_CLIENT_ID: string; // app SPA web (public client) propre à l'env
  AUTH_MOBILE_CLIENT_ID: string; // app mobile (native)
  AUTH_API_SCOPE: string; // scope API délégué (Stories.Read)
  AUTH_GOOGLE_WEB_CLIENT_ID: string; // client OAuth Web Google (Sign-In DIRECT, hors Entra)
  // --- domaine API (backend cc-attack) ---
  API_BASE: string;
}

// Communs à tous les environnements.
const AUTH_TENANT_ID = '149f9d8c-128e-40a1-8872-2a4add87042c';
const AUTH_TENANT_SUBDOMAIN = 'cloudchronociam';
const AUTH_MOBILE_CLIENT_ID = 'cf495129-0de2-47e7-a265-8cf4e1854575'; // cc-attack-jaw-mobile-react
// Un seul projet/client Google (Web) pour tous les envs. Sert de client_id GIS (web)
// ET de serverClientId (Android) -> aud identique cote backend.
const GOOGLE_WEB_CLIENT_ID = '69212378182-uvgpfl8lrvlam2k0711dtn0ae0s9qadi.apps.googleusercontent.com';
const SPA_DEV = '0a3782ec-1e70-4228-bf3b-b5863a6d2ee2'; // cc-attack-jaw-spa-react-dev
const SPA_MAIN = '9b473cfb-1bdb-447c-898f-6713feb98a94'; // cc-attack-jaw-spa-react-main

const CONFIGS: Record<AppEnv, EnvConfig> = {
  // En local : app SPA dev (rattachée au flow signup + redirect localhost) + backend local.
  local: {
    env: 'local',
    AUTH_TENANT_ID,
    AUTH_TENANT_SUBDOMAIN,
    AUTH_SPA_CLIENT_ID: SPA_DEV,
    AUTH_MOBILE_CLIENT_ID,
    AUTH_API_SCOPE: 'api://cc-attack-backend-dev/Stories.Read',
    AUTH_GOOGLE_WEB_CLIENT_ID: GOOGLE_WEB_CLIENT_ID,
    API_BASE: 'http://localhost:8080',
  },
  dev: {
    env: 'dev',
    AUTH_TENANT_ID,
    AUTH_TENANT_SUBDOMAIN,
    AUTH_SPA_CLIENT_ID: SPA_DEV,
    AUTH_MOBILE_CLIENT_ID,
    AUTH_API_SCOPE: 'api://cc-attack-backend-dev/Stories.Read',
    AUTH_GOOGLE_WEB_CLIENT_ID: GOOGLE_WEB_CLIENT_ID,
    API_BASE: 'https://aca-cc-attack-image-dev-app.happysky-1c894a90.westeurope.azurecontainerapps.io',
  },
  main: {
    env: 'main',
    AUTH_TENANT_ID,
    AUTH_TENANT_SUBDOMAIN,
    AUTH_SPA_CLIENT_ID: SPA_MAIN,
    AUTH_MOBILE_CLIENT_ID,
    AUTH_API_SCOPE: 'api://cc-attack-backend-main/Stories.Read',
    AUTH_GOOGLE_WEB_CLIENT_ID: GOOGLE_WEB_CLIENT_ID,
    API_BASE: 'https://aca-cc-attack-image-main-app.nicepebble-da50171d.westeurope.azurecontainerapps.io',
  },
};

function isEnv(v: string | null | undefined): v is AppEnv {
  return v === 'local' || v === 'dev' || v === 'main';
}

function detectEnv(): AppEnv {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const w = window as unknown as { __CCJAW_ENV__?: string };
    if (isEnv(w.__CCJAW_ENV__)) return w.__CCJAW_ENV__;
    try {
      // ?env=dev est MÉMORISÉ (localStorage) -> survit aux redirections OAuth
      // (le retour Google sur /auth/google/callback n'a pas le query string).
      const q = new URLSearchParams(window.location.search).get('env');
      if (isEnv(q)) {
        try { localStorage.setItem('ccjaw.env', q); } catch { /* ignore */ }
        return q;
      }
      const stored = localStorage.getItem('ccjaw.env');
      if (isEnv(stored)) return stored;
    } catch {
      /* ignore */
    }
    const h = (window.location.hostname || '').toLowerCase();
    if (h === 'dev.cloudchrono.com') return 'dev';
    if (h === 'main.cloudchrono.com' || h === 'cloudchrono.com' || h === 'www.cloudchrono.com') return 'main';
    return 'local';
  }
  // Natif (iOS/Android) : pas de hostname -> l'env est figé AU BUILD dans
  // env.generated.ts (écrit par scripts/set-native-env.mjs). Défaut 'dev'.
  return isEnv(NATIVE_ENV) ? NATIVE_ENV : 'dev';
}

let _cfg: EnvConfig | null = null;
/** Config de l'environnement courant (résolue une fois, mémoïsée). */
export function getEnvConfig(): EnvConfig {
  if (!_cfg) _cfg = CONFIGS[detectEnv()];
  return _cfg;
}
export function currentEnv(): AppEnv {
  return getEnvConfig().env;
}

/** Base de l'API : surcharge ponctuelle possible via window.__CCJAW_API_URL__. */
export function resolveApiBase(): string {
  if (typeof window !== 'undefined') {
    const override = (window as unknown as { __CCJAW_API_URL__?: string }).__CCJAW_API_URL__;
    if (override) return String(override).replace(/\/+$/, '');
  }
  return getEnvConfig().API_BASE.replace(/\/+$/, '');
}
